import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Box,
  IconButton,
  Pagination,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import SearchIcon from "@mui/icons-material/Search";
import Swal from "sweetalert2";
import { getEvidencias, updateEvidencia } from "../services/evidenciasService";

const rowsPerPage = 5;

const getEvidenceId = (ev) =>
  ev?.idevidencias ?? ev?.id ?? ev?.Id ?? ev?.ID ?? ev?.idEvidencia ?? ev?.IdEvidencia ?? ev?.Id_Evidencia;

/** Normaliza Estado: la API puede devolver 1, "1", true, etc. */
const isEstadoActivo = (estado) => Number(estado) === 1 || estado === true || estado === "true";

export default function TableEvidencias() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [evidencias, setEvidencias] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEvidencias = async () => {
    try {
      setLoading(true);
      const data = await getEvidencias();
      const list = Array.isArray(data) ? data : data?.data ?? data?.evidencias ?? [];
      setEvidencias(list);
    } catch (error) {
      console.error("Error al cargar evidencias", error);
      setEvidencias([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvidencias();
  }, []);

  const handleEditClick = (e, evidencia) => {
    e.preventDefault();
    e.stopPropagation();
    const id = getEvidenceId(evidencia);
    if (id != null && id !== "") {
      navigate("/Evidencias/editar/" + encodeURIComponent(String(id)), { state: { evidencia } });
    } else {
      Swal.fire("Aviso", "No se pudo obtener el ID de la evidencia. ¿Tu API devuelve id, Id o idEvidencia?", "info");
    }
  };

  const handleDesactivar = async (evidencia) => {
    const id = getEvidenceId(evidencia);
    if (id == null) return;

    const result = await Swal.fire({
      title: "¿Desactivar evidencia?",
      text: "La evidencia no se mostrará como activa. Puedes reactivarla editándola después.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, desactivar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      const payload = {
        Seccion: evidencia.Seccion ?? "",
        Titular: evidencia.Titular ?? "",
        SubTitulo: evidencia.SubTitulo ?? "",
        Cuerpo: evidencia.Cuerpo ?? "",
        Fuente: evidencia.Fuente ?? "",
        Imagen: evidencia.Imagen ?? "",
        Fecha: evidencia.Fecha ?? "",
        Estado: 0,
      };
      await updateEvidencia(id, payload);
      await fetchEvidencias();
      Swal.fire("Listo", "La evidencia ha sido desactivada.", "success");
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || "No se pudo desactivar.";
      Swal.fire("Error", msg, "error");
    }
  };

  const filtered = evidencias.filter((ev) => {
    const s = search.toLowerCase();
    return (
      (ev.Seccion ?? "").toLowerCase().includes(s) ||
      (ev.Titular ?? "").toLowerCase().includes(s) ||
      (ev.SubTitulo ?? "").toLowerCase().includes(s) ||
      (ev.Cuerpo ?? "").toLowerCase().includes(s)
    );
  });

  const paginated = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));

  return (
    <Box mb={4}>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <FactCheckIcon color="primary" />
        <Typography variant="h6">Evidencias y resultados creados</Typography>
      </Box>

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <TextField
          size="small"
          placeholder="Buscar por sección, título, contenido..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          backgroundColor: "white",
          borderRadius: 2,
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Sección</TableCell>
              <TableCell>Título</TableCell>
              <TableCell>Subtítulo</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary">Cargando...</Typography>
                </TableCell>
              </TableRow>
            ) : paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary">No hay evidencias registradas.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((ev, index) => (
                <TableRow key={getEvidenceId(ev) ?? index}>
                  <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                  <TableCell>{ev.Seccion || "—"}</TableCell>
                  <TableCell sx={{ maxWidth: 180 }}>{ev.Titular || "—"}</TableCell>
                  <TableCell sx={{ maxWidth: 160 }}>
                    <Typography noWrap title={ev.SubTitulo || ""}>
                      {ev.SubTitulo ? (ev.SubTitulo.length > 25 ? `${ev.SubTitulo.slice(0, 25)}…` : ev.SubTitulo) : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>{ev.Fecha ? ev.Fecha.slice(0, 10) : "—"}</TableCell>
                  <TableCell>
                    <Chip
                      label={isEstadoActivo(ev.Estado) ? "Activo" : "Inactivo"}
                      size="small"
                      variant="outlined"
                      color={isEstadoActivo(ev.Estado) ? "success" : "default"}
                      sx={
                        !isEstadoActivo(ev.Estado)
                          ? {
                              bgcolor: "grey.100",
                              color: "grey.600",
                              borderColor: "grey.400",
                              "& .MuiChip-label": { fontWeight: 500 },
                            }
                          : {
                              borderColor: "success.main",
                              color: "success.dark",
                              bgcolor: "success.light",
                              "& .MuiChip-label": { fontWeight: 500 },
                            }
                      }
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <IconButton
                        type="button"
                        color="primary"
                        size="small"
                        onClick={(e) => handleEditClick(e, ev)}
                        aria-label="Editar evidencia"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Desactivar">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleDesactivar(ev)}
                        disabled={!isEstadoActivo(ev.Estado)}
                      >
                        <BlockIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
}
